import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createSystemAgent,
  getAdminAgent,
  getAdminAgentFileTools,
  getAdminAgentStats,
  getAdminAgents,
  publishAdminAgent,
  uploadAdminAgentAvatar,
  updateAdminAgent,
  updateAdminAgentFileTool,
  updateAdminAgentStatus,
} from '../api/adminAgents.api';
import type {
  AdminAgentDetail,
  AdminAgentFileToolPermission,
  AdminAgentFileToolPermissionsResponse,
  CreateSystemAgentInput,
  ListAdminAgentsParams,
  ListAdminAgentsResponse,
  UpdateAdminAgentFileToolPermissionInput,
  UpdateAdminAgentInput,
  UpdateAdminAgentStatusInput,
} from '../types';

export const adminAgentKeys = {
  all: ['admin', 'agents'] as const,
  stats: () => [...adminAgentKeys.all, 'stats'] as const,
  lists: () => [...adminAgentKeys.all, 'list'] as const,
  list: (params?: ListAdminAgentsParams) => [...adminAgentKeys.lists(), params] as const,
  details: () => [...adminAgentKeys.all, 'detail'] as const,
  detail: (publicId: string) => [...adminAgentKeys.details(), publicId] as const,
  fileTools: (publicId: string) => [...adminAgentKeys.detail(publicId), 'file-tools'] as const,
};

export const useAdminAgentStats = () => {
  return useQuery({
    queryKey: adminAgentKeys.stats(),
    queryFn: getAdminAgentStats,
    staleTime: 30_000,
  });
};

export const useAdminAgents = (params?: ListAdminAgentsParams) => {
  return useQuery<ListAdminAgentsResponse, Error>({
    queryKey: adminAgentKeys.list(params),
    queryFn: () => getAdminAgents(params),
    staleTime: 10_000,
  });
};

export const useAdminAgent = (publicId?: string) => {
  return useQuery<AdminAgentDetail, Error>({
    queryKey: adminAgentKeys.detail(publicId || ''),
    queryFn: () => getAdminAgent(publicId || ''),
    enabled: Boolean(publicId),
    staleTime: 10_000,
  });
};

export const useCreateSystemAgent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateSystemAgentInput) => createSystemAgent(data),
    onSuccess: (agent) => {
      queryClient.invalidateQueries({ queryKey: adminAgentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: adminAgentKeys.stats() });
      queryClient.setQueryData(adminAgentKeys.detail(agent.publicId), agent);
    },
  });
};

export const useUpdateAdminAgent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, data }: { publicId: string; data: UpdateAdminAgentInput }) =>
      updateAdminAgent(publicId, data),
    onSuccess: (agent) => {
      queryClient.invalidateQueries({ queryKey: adminAgentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: adminAgentKeys.stats() });
      queryClient.setQueryData(adminAgentKeys.detail(agent.publicId), agent);
    },
  });
};

export const useUpdateAdminAgentStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, data }: { publicId: string; data: UpdateAdminAgentStatusInput }) =>
      updateAdminAgentStatus(publicId, data),
    onSuccess: (agent) => {
      queryClient.invalidateQueries({ queryKey: adminAgentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: adminAgentKeys.stats() });
      queryClient.setQueryData(adminAgentKeys.detail(agent.publicId), agent);
    },
  });
};

export const usePublishAdminAgent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => publishAdminAgent(publicId),
    onSuccess: (agent) => {
      queryClient.invalidateQueries({ queryKey: adminAgentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: adminAgentKeys.stats() });
      queryClient.setQueryData(adminAgentKeys.detail(agent.publicId), agent);
    },
  });
};

export const useUploadAdminAgentAvatar = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadAdminAgentAvatar(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminAgentKeys.lists() });
    },
  });
};

export const useAdminAgentFileTools = (publicId?: string, enabled = true) => {
  return useQuery<AdminAgentFileToolPermissionsResponse, Error>({
    queryKey: adminAgentKeys.fileTools(publicId || ''),
    queryFn: () => getAdminAgentFileTools(publicId || ''),
    enabled: Boolean(publicId) && enabled,
    staleTime: 10_000,
  });
};

export const useUpdateAdminAgentFileTool = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      publicId,
      toolId,
      data,
    }: {
      publicId: string;
      toolId: AdminAgentFileToolPermission['toolId'];
      data: UpdateAdminAgentFileToolPermissionInput;
    }) => updateAdminAgentFileTool(publicId, toolId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: adminAgentKeys.fileTools(variables.publicId) });
      queryClient.invalidateQueries({ queryKey: adminAgentKeys.detail(variables.publicId) });
    },
  });
};
